import { computeSky, SKY_RADIUS } from '@/components/Editor/StarMap/sky';
import { CUTOUT_SQUARE, SHAPE_CUTOUTS } from '@/components/Editor/Shared/shapes';
import { getTypoScale } from '@/components/UI/FontFamilySelector/fontMetrics';

// Layered wooden star map: a piece cut in the chosen shape (`top` mask, white = wood) sits on the backplate,
// with the sky engraved into it (`engraving`, white marks on transparent). Optional `overlay` is a second
// cut ply of letter shapes from the same fonts as the poster editor.
export const MASK_WIDTH = 2048;
// side of the square the shape is drawn in, and its centre height, relative to the board width / height
const WINDOW_SIZE = 0.8;
const WINDOW_CENTER = 0.43;
// all in mask px
const CONSTELLATION_WIDTH = 3;
const GRID_WIDTH = 1.5;
// faint stars still engrave as a visible dot
const MIN_STAR_GROWTH = 0.75;
const TEXT_WIDTH = 0.7;
const TEXT_SIZE = 0.08;
// same plaque as Preview3d.label(): width is 0.26 of the board, height is 0.28 of that, centre 1.1 ply-heights up from the bottom
const PLAQUE_WIDTH = 0.26;
const PLAQUE_HEIGHT = 0.28;
const PLAQUE_LIFT = 1.1;

function canvas(height) {
    const element = document.createElement('canvas');
    element.width = MASK_WIDTH;
    element.height = height;
    const ctx = element.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    return { element, ctx };
}

export function fontFromTypo(typo, sizePx) {
    if (typeof document === 'undefined') {
        return { font: `700 ${sizePx}px Figtree, sans-serif`, uppercase: false };
    }
    const probe = document.createElement('span');
    probe.className = typo?.class || 'font-Figtree font-bold';
    probe.style.cssText = 'position:absolute;visibility:hidden;font-size:100px';
    document.body.appendChild(probe);
    const { fontStyle, fontWeight, fontFamily, textTransform } = getComputedStyle(probe);
    document.body.removeChild(probe);
    return {
        font: `${fontStyle} ${fontWeight} ${sizePx}px ${fontFamily}`,
        uppercase: textTransform === 'uppercase',
    };
}

function wrapLines(ctx, text, maxWidth) {
    const lines = [];
    text.split(/\s+/).forEach((word) => {
        const next = lines.length ? `${lines[lines.length - 1]} ${word}` : word;
        if (lines.length && ctx.measureText(next).width > maxWidth) lines.push(word);
        else if (lines.length) lines[lines.length - 1] = next;
        else lines.push(word);
    });
    return lines;
}

function textCenterY(height) {
    const cutoutBottom = height * WINDOW_CENTER + (MASK_WIDTH * WINDOW_SIZE) / 2;
    const plaqueH = MASK_WIDTH * PLAQUE_WIDTH * PLAQUE_HEIGHT;
    const plaqueTop = height - plaqueH * (PLAQUE_LIFT + 0.5);
    return (cutoutBottom + plaqueTop) / 2;
}

function drawTextLayer(text, typo, height, textSize) {
    const size = MASK_WIDTH * TEXT_SIZE * getTypoScale(typo) * (textSize || 1);
    const { font, uppercase } = fontFromTypo(typo, size);
    const overlay = canvas(height);
    const { ctx } = overlay;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, MASK_WIDTH, height);
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = font;
    const lines = wrapLines(ctx, uppercase ? text.toUpperCase() : text, MASK_WIDTH * TEXT_WIDTH);
    const lineHeight = size * 1.15;
    const top = textCenterY(height) - ((lines.length - 1) * lineHeight) / 2;
    lines.forEach((line, index) => ctx.fillText(line, MASK_WIDTH / 2, top + index * lineHeight, MASK_WIDTH * TEXT_WIDTH));
    return overlay.element;
}

export async function drawStarLayers({ lat, lng, time, shape, parts, aspect, text, typo, textSize }) {
    const trimmed = (text || '').trim();
    if (typeof document !== 'undefined' && document.fonts) {
        await document.fonts.ready;
        if (trimmed && typo) {
            try {
                await document.fonts.load(fontFromTypo(typo, 100).font, trimmed);
            } catch (error) {}
        }
    }

    const height = Math.round(MASK_WIDTH / aspect);
    const side = MASK_WIDTH * WINDOW_SIZE;
    const shapePath = new Path2D(SHAPE_CUTOUTS[shape] || SHAPE_CUTOUTS.shape_circle);
    const toWindow = (ctx) => ctx.translate(MASK_WIDTH / 2, height * WINDOW_CENTER);
    // shapes are traced around CUTOUT_SQUARE on the poster's 1920 canvas
    const toShape = (ctx) => {
        toWindow(ctx);
        ctx.scale(side / CUTOUT_SQUARE.size, side / CUTOUT_SQUARE.size);
        ctx.translate(-CUTOUT_SQUARE.cx, -CUTOUT_SQUARE.cy);
    };

    const top = canvas(height);
    top.ctx.fillStyle = '#000';
    top.ctx.fillRect(0, 0, MASK_WIDTH, height);
    toShape(top.ctx);
    top.ctx.fillStyle = '#fff';
    top.ctx.fill(shapePath);

    const engraving = canvas(height);
    const { ctx } = engraving;
    toShape(ctx);
    ctx.clip(shapePath);
    // the clip stays, only the transform is reset
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    toWindow(ctx);
    // sky units are centred on the zenith with the horizon at SKY_RADIUS; the shape's square spans the horizon
    const scale = side / (SKY_RADIUS * 2);
    ctx.scale(scale, scale);
    // a shape's corners reach past the horizon circle, so draw the sky somewhat below it there
    const sky = computeSky(lat, lng, time, -0.35);
    ctx.strokeStyle = ctx.fillStyle = '#fff';
    if (parts.grid) {
        ctx.lineWidth = GRID_WIDTH / scale;
        ctx.stroke(new Path2D(sky.grid));
    }
    if (parts.constellations) {
        ctx.lineWidth = CONSTELLATION_WIDTH / scale;
        ctx.stroke(new Path2D(sky.constellations));
    }
    if (parts.stars) {
        const stars = new Path2D(sky.stars);
        ctx.lineWidth = (MIN_STAR_GROWTH * 2) / scale;
        ctx.fill(stars);
        ctx.stroke(stars);
    }
    return {
        top: top.element,
        engraving: engraving.element,
        overlay: trimmed ? drawTextLayer(trimmed, typo, height, textSize) : null,
    };
}
